/**
 * Wordmark — production logo image lockup. Phase 1 primary identity per
 * `/design/Phase1_RELOCK_Decisions.md` §2.1 (Option C).
 *
 * Locked grammar references:
 *   /design/peopleprints-logo-on-light.png  (canonical light-bg variant)
 *   /design/peopleprints-logo-on-dark.png   (canonical dark-bg variant)
 *
 * The two variants tune wordmark contrast for surface luminance — light-bg
 * version uses navy-weight wordmark elements for legibility on white,
 * dark-bg version uses lighter wordmark elements for legibility on navy.
 * Don't cross the streams; using the wrong variant on the wrong background
 * degrades the wordmark contrast significantly.
 *
 * Vite serves /public/* at the site root, so the logo is referenced by
 * absolute path. Per-tenant theming applies via the surrounding CSS
 * custom properties on adjacent surfaces, but the logo PNG itself is
 * brand-locked and not theme-overridable in V1 (white-label tenants
 * supply their own logo asset via the tenant-theme admin).
 *
 * Props:
 *   scale   — multiplier on the natural display height (default 1 = 32px tall)
 *   variant — "light" (default, for white/light surfaces) or "dark" (for navy/dark)
 *
 * The DatumMark + ContourMark SVG marks were the pre-PNG candidates;
 * `/design/Phase1_RELOCK_Decisions.md` §2.1 consolidated on Option C
 * (this image-based component — no parallel-track V2 mark in V1). Both
 * SVG marks are deprecated as of 2026-05-04 and unused — still exported
 * from `/components/index.js` but a removal candidate for V1.x cleanup.
 * See their file headers before re-enabling either.
 */

import { useEffect, useState } from "react";

// Track the app's night-mode flag (App.jsx sets data-theme="dark" on <html>)
// so the wordmark auto-swaps to the light-text variant on dark surfaces. An
// explicit `variant` prop still wins for the rare fixed-surface caller.
function useThemeVariant() {
  const read = () =>
    (typeof document !== "undefined" &&
      document.documentElement.getAttribute("data-theme") === "dark")
      ? "dark"
      : "light";
  const [v, setV] = useState(read);
  useEffect(() => {
    const el = document.documentElement;
    const update = () => setV(read());
    update();
    const obs = new MutationObserver(update);
    obs.observe(el, { attributes: true, attributeFilter: ["data-theme"] });
    return () => obs.disconnect();
  }, []);
  return v;
}

export default function Wordmark({ scale = 1, variant }) {
  // Source PNG natural dimensions: 1024×315 (v2 wordmark, 2026-05-07 redesign).
  // Aspect ratio is significantly more horizontal than v1's 1024×559 — wordmark
  // text now reads cleanly at small scales without glyph-vs-text contention.
  // Display at 32px tall × scale.
  const displayHeight = 32 * scale;
  const aspectRatio = 1024 / 257; // Cersosimo CA-Logo lockup
  const displayWidth = displayHeight * aspectRatio;

  const themeVariant = useThemeVariant();
  const resolved = variant || themeVariant; // explicit prop overrides auto-detect

  const src = resolved === "dark"
    ? "/cersosimo-logo-light.png"
    : "/CA-Logo.png";

  return (
    <img
      src={src}
      alt="Cersosimo · Decision Science & Engineering"
      width={displayWidth}
      height={displayHeight}
      style={{
        display: "block",
        height: displayHeight,
        width: "auto",
        maxWidth: "100%",
        userSelect: "none",
      }}
      draggable={false}
      data-component="Wordmark"
      data-variant={resolved}
    />
  );
}
